import { Dispatch, FC, PropsWithChildren, RefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { RootState } from 'store/rootReducer';
import { setActiveOrganisationAndRoles } from 'store/userSlice';
import { getPagaendeAktiviteter } from 'store/utforarePagaendeAktiviteterSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaCookies, RaaDialog, useOutsideClick } from 'raa-react';

import { ButtonList, ExternalLink } from 'components/sharedElements/sharedElements';

import { Authority } from 'models';

import { AktivitetstypEnum, AuthorityRolesEnum } from 'enums';

import { usePostAktivitetEnkelInventeringMutation } from 'api/endpoints/aktivitetPreliminarSlice';
import classNames from 'classnames';
import sha256 from 'crypto-js/sha256';
import { baseHref } from 'utilities/getBaseHref';

const Menu: FC<MenuProps> = (props: PropsWithChildren<MenuProps>) => {
  const menuRef = useRef<HTMLDivElement>(null);

  const [parentBottom, setParentBottom] = useState<number>(0);
  const [showOrgDialog, setShowOrgDialog] = useState<boolean>(false);
  const [showCookies, setShowCookies] = useState<boolean>(false);

  const {
    data: userData,
    selected_organisation: selectedOrg,
    hasNoAuthorization,
    isAnonymous,
    rolesInSelectedOrganisation,
    requestStatus: userRequestStatus
  } = useAppSelector((state: RootState) => state.user);
  const registrationActive = useAppSelector((state: RootState) => state.aktivitet.registrationActive);
  const { data: pagaendeAktiviteter } = useAppSelector((state: RootState) => state.utforarePagaendeAktiviteter);
  const { data: publiceradeObjekt } = useAppSelector((state: RootState) => state.publiceradeBebyggelseobjekt);
  const aktivitetDomaner = useAppSelector((state: RootState) => state.aktivitetDomaner.data);

  const { setDialog, unsetDialog } = useDialog();

  const dispatch = useAppDispatch();
  const outsideClickRefs: RefObject<HTMLDivElement> | RefObject<HTMLElement>[] = props.triggerRef
    ? [props.triggerRef, menuRef]
    : menuRef;
  const [isOutsideClick, setClicked] = useOutsideClick(outsideClickRefs);
  const [postEnkelInventering, { data: enkelinventeringAktivitet, isSuccess: postEnkelInventeringIsSuccess }] =
    usePostAktivitetEnkelInventeringMutation();

  const navigate = useNavigate();
  const location = useLocation();

  const showPagaendeRegistrering =
    rolesInSelectedOrganisation.includes(AuthorityRolesEnum.BEBYGGELSEREGISTRERARE) ||
    rolesInSelectedOrganisation.includes(AuthorityRolesEnum.SBM_HANDLAGGARE);

  useEffect(() => {
    if (props.parent.current) {
      setParentBottom(props.parent.current?.getBoundingClientRect().bottom);
    }
  }, [props]);

  useEffect(() => {
    if (postEnkelInventeringIsSuccess && enkelinventeringAktivitet) {
      navigate(`/aktivitet/${enkelinventeringAktivitet.aktivitet_id}`);
    }
  }, [enkelinventeringAktivitet, navigate, postEnkelInventeringIsSuccess]);

  useEffect(() => {
    if (isOutsideClick) {
      setClicked(false);
      props.onOutsideClick();
    }
  }, [isOutsideClick, props, setClicked]);

  useEffect(() => {
    if (!hasNoAuthorization && selectedOrg && props.open && showPagaendeRegistrering) {
      dispatch(getPagaendeAktiviteter(selectedOrg.organisation_nummer));
    }
  }, [selectedOrg, dispatch, publiceradeObjekt, hasNoAuthorization, props.open, showPagaendeRegistrering]);

  const handleAuthoritySelection = (authority: Authority): void => {
    try {
      if (userData?.username) {
        const hash = sha256(userData.username).toString();
        localStorage.setItem(hash, authority.organisation_nummer);
      }
    } catch (error) {
      console.error(error);
    }
    dispatch(setActiveOrganisationAndRoles({ selectedOrganisation: authority, user: userData }));
    setShowOrgDialog(false);
  };

  return (
    <>
      {userRequestStatus === 'idle' && userData && (
        <CSSTransition nodeRef={menuRef} in={props.open} unmountOnExit timeout={0}>
          <div
            id="meny"
            aria-label="huvudmeny"
            ref={menuRef}
            className="tw-absolute tw-right-0 tw-z-[100] tw-flex tw-flex-col tw-gap-4 tw-border tw-border-t-0 tw-border-raa-gray-3 tw-bg-raa-white tw-p-8"
            style={{ top: parentBottom }}
          >
            {!isAnonymous && (
              <div className="tw-flex tw-flex-col tw-gap-4">
                <div className="tw-flex tw-flex-col tw-text-raa-gray-6">
                  <p className="tw-font-semibold">{userData.name}</p>
                  <p>{selectedOrg?.organisation_namn}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowOrgDialog(true);
                      props.onOutsideClick();
                    }}
                    className={classNames('tw-text-left', {
                      'tw-text-raa-gray-4': registrationActive,
                      'raa-link': !registrationActive
                    })}
                    disabled={registrationActive}
                  >
                    Byt organisation
                  </button>
                </div>

                {showPagaendeRegistrering && (
                  <>
                    {pagaendeAktiviteter && pagaendeAktiviteter.length > 0 ? (
                      <Link to={'/aktiviteter'} onClick={() => props.setShowMenu(false)} className="raa-link">
                        <span className="raa-icon-edit raa-icon tw-mr-1" />
                        Pågående registrering ({pagaendeAktiviteter.length})
                      </Link>
                    ) : (
                      <span className="tw-text-raa-gray-4">
                        <span className="raa-icon-edit raa-icon tw-mr-1" />
                        Pågående registrering (0)
                      </span>
                    )}
                  </>
                )}

                {rolesInSelectedOrganisation.includes(AuthorityRolesEnum.BEBYGGELSEREGISTRERARE) && (
                  <div className="tw-flex tw-flex-col tw-gap-1">
                    <p>Nyregistrering</p>

                    <Link to={'/inventering'} onClick={() => props.setShowMenu(false)} className="raa-link">
                      <span className="raa-icon-add raa-icon tw-mr-1" />
                      Inventeringsprojekt
                    </Link>

                    {selectedOrg && (
                      <button
                        onClick={() => {
                          props.setShowMenu(false);
                          setDialog(RaaDialog, {
                            header: 'Vill du skapa en inventering av ett enskilt objekt?',
                            proceed: () => {
                              unsetDialog();
                              postEnkelInventering({
                                organisationsnummer: selectedOrg.organisation_nummer
                              });
                            },
                            close: unsetDialog,
                            proceedButtonText: 'skapa',
                            cancel: unsetDialog,
                            cancelButtonText: 'avbryt',
                            ariaDescribedBy: 'dialog-description',
                            children: (
                              <p id="dialog-description" className="tw-mt-4">
                                {
                                  aktivitetDomaner?.aktivitettyper.find(
                                    (typ) => typ.aktivitettyp === AktivitetstypEnum.ENKELINVENTERING
                                  )?.beskrivning
                                }
                              </p>
                            )
                          });
                        }}
                        className="raa-link tw-text-left"
                      >
                        <span className="raa-icon-add raa-icon tw-mr-1" />
                        Inventering av enskilt objekt
                      </button>
                    )}
                  </div>
                )}

                {rolesInSelectedOrganisation.includes(AuthorityRolesEnum.SBM_HANDLAGGARE) && (
                  <div className="tw-flex tw-flex-col tw-items-start">
                    <p>Nyregistrering</p>

                    <Link to={'/skyddsklassning'} onClick={() => props.setShowMenu(false)} className="raa-link">
                      <span className="raa-icon-add raa-icon tw-mr-1" />
                      Statligt byggnadsminne
                    </Link>
                  </div>
                )}
              </div>
            )}

            <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
              {isAnonymous && (
                <>
                  <a href={`/id/secure/bebyggelse${location.pathname.replace(baseHref, '')}`} className="raa-link">
                    Logga in
                  </a>
                  <p className="raa-secondary-text tw-mb-4">För registrerande organisationer</p>
                </>
              )}

              <ExternalLink
                href="https://www.raa.se/hitta-information/bebyggelseregistret/vanliga-fragor-om-bebyggelseregistret"
                text="Hjälp"
              />

              <ExternalLink
                href="https://www.raa.se/hitta-information/bebyggelseregistret-bebr"
                text="Om Bebyggelseregistret"
              />

              <ExternalLink
                href="https://www.raa.se/hitta-information/oppna-data/oppna-data-portal"
                text="Öppna data-portalen"
              />
              <ExternalLink
                href="https://www.raa.se/om-riksantikvarieambetet/om-webbplatsen/tillganglighetsredogorelse"
                text="Tillgänglighetsredogörelse"
              />

              <ExternalLink href="https://www.raa.se" text="Riksantikvarieämbetet" />
            </div>

            <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
              <a className="raa-link" href="mailto:bebyggelseregistret@raa.se">
                <span className="raa-icon-mail raa-icon tw-mr-1" />
                bebyggelseregistret@raa.se
              </a>

              <a
                className="raa-link"
                onClick={() => {
                  setShowCookies(true);
                  props.onOutsideClick();
                }}
              >
                Cookie-inställningar
              </a>

              {!isAnonymous && (
                <a className="raa-link tw-mt-4" href={`${userData?.logout_url}?redirect_uri=/open/bebyggelse`}>
                  Logga ut
                </a>
              )}
            </div>
          </div>
        </CSSTransition>
      )}

      {showOrgDialog && userData && (
        <RaaDialog
          header="Välj organisation"
          close={() => setShowOrgDialog(false)}
          ariaDescribedBy="dialog-description"
        >
          <p id="dialog-description" className="tw-pb-4">
            {userData?.name}
          </p>
          <ButtonList
            disabledKey={selectedOrg?.organisation_nummer}
            items={Array.from(
              new Map(
                userData?.authorities.map((authority: Authority) => [authority.organisation_nummer, authority])
              ).values()
            )}
            selectedItem={(item) => handleAuthoritySelection(item)}
            displayAttr="organisation_namn"
            valueAttr="organisation_nummer"
          />
        </RaaDialog>
      )}

      <RaaCookies
        statistics={true}
        functional={true}
        thirdParty={true}
        showBottomDialog={showCookies}
        setShowBottomDialog={setShowCookies}
        language="sv"
      />
    </>
  );
};

export interface MenuProps {
  parent: RefObject<HTMLElement>;
  open: boolean;
  setShowMenu: Dispatch<SetStateAction<boolean>>;
  onOutsideClick: () => void;
  triggerRef?: RefObject<HTMLElement>;
}

export default Menu;
